import { notifications } from "@mantine/notifications";
import { authApi } from "./api";

let cachedGoogleClientId: string | null | undefined;
let gisInitializedForClientId: string | null = null;
const GOOGLE_BUTTON_SHAPE = "rectangular";
const GOOGLE_BUTTON_MAX_WIDTH_TOKEN = "--axio-unit-400";

function getGoogleButtonWidth(parent: HTMLElement): string | undefined {
  const availableWidth = parent.getBoundingClientRect().width;
  const maximumWidth = Number.parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue(GOOGLE_BUTTON_MAX_WIDTH_TOKEN),
  );

  const resolvedWidth = Number.isFinite(maximumWidth)
    ? Math.min(availableWidth || maximumWidth, maximumWidth)
    : availableWidth;

  return resolvedWidth > 0 ? String(Math.round(resolvedWidth)) : undefined;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: Record<string, unknown>) => void;
          renderButton: (parent: HTMLElement, config: Record<string, unknown>) => void;
          cancel: () => void;
        };
      };
    };
  }
}

export function loadGisScript(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Failed to load Google script")));
      return;
    }
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Google script"));
    document.head.appendChild(script);
  });
}

export async function getGoogleClientId(): Promise<string | null> {
  if (cachedGoogleClientId !== undefined) return cachedGoogleClientId;
  try {
    const config = await authApi.publicConfig();
    cachedGoogleClientId = config.google_enabled ? config.google_client_id : null;
  } catch {
    cachedGoogleClientId = null;
  }
  return cachedGoogleClientId;
}

export async function mountGoogleButton(
  parent: HTMLElement,
  onCredential: (idToken: string) => void,
): Promise<void> {
  const googleClientId = await getGoogleClientId();
  if (!googleClientId) return;

  await loadGisScript();

  if (gisInitializedForClientId !== googleClientId) {
    window.google!.accounts.id.initialize({
      client_id: googleClientId,
      callback: (response: { credential: string }) => onCredential(response.credential),
      auto_select: false,
      cancel_on_tap_outside: true,
    });
    gisInitializedForClientId = googleClientId;
  }

  parent.innerHTML = "";
  const width = getGoogleButtonWidth(parent);
  window.google!.accounts.id.renderButton(parent, {
    type: "standard",
    theme: "outline",
    size: "large",
    text: "continue_with",
    logo_alignment: "center",
    ...(width ? { width } : {}),
    shape: GOOGLE_BUTTON_SHAPE,
  });
}

export function notifyGoogleLoadError(err: unknown): void {
  const message = err instanceof Error ? err.message : "Could not load Google sign-in";
  notifications.show({ color: "red", title: "Failed", message });
}
